import { Language } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Divider, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { GB, IT, SI } from 'country-flag-icons/react/3x2';
import I18n, { changeLanguage } from 'i18next';
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Background from "../../assets/background.png";
import LoginLogo from "../../assets/beetogether-logo-520x520.png";
import Logo from "../../assets/beetogether-logo-extended.png";
import { CustomDialog } from "../../common/components/CustomDialog";
import CustomForm from "../../common/components/CustomForm";
import CustomLoader from "../../common/components/CustomLoader";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import { getForm } from "../../common/config/forms";
import { useAuth } from "../../common/hooks/useAuth";
import { useLoading } from "../../common/hooks/useLoading";
import { ILoginBody, IStartChangePasswordBody } from "../../common/interfaces/auth";
import { ICustomDialogProps } from "../../common/interfaces/dialog";
import authService from "../../common/services/authService";

interface ILoginProps { }

const Login: FC<ILoginProps> = () => {
    // hooks
    const { login, setPermissions } = useAuth();
    const { loading } = useLoading();
    const locale = I18n.language;
    const { t } = useTranslation();
    const location = useLocation();

    const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
    const [language, setLanguage] = useState(locale);

    const lang = [
        { flag: <GB title="English" />, label: I18n.t("EN", { locale: language }), value: "en-EN" },
        { flag: <IT title="Italy" />, label: I18n.t("IT", { locale: language }), value: "it-IT" },
        { flag: <SI title="Slovenian" />, label: I18n.t("SL", { locale: language }), value: "sl-SI" },
    ]

    // form
    const signInForm = getForm<ILoginBody>("login");
    const startChangePasswordForm = getForm<IStartChangePasswordBody>("startChangePassword");

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">(
        "success"
    );

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);

    const initialValuesSignIn: ILoginBody = {
        emailAddress: "",
        password: "",
    };

    const initialValuesStartChangePassword: IStartChangePasswordBody = {
        emailAddress: ""
    };

    useEffect(() => {
        if (location.state && location.state.from === "register") {
            setMessage(t("register.successMessage"));
            setResponseType("success");
            setOpenSnackbar(true);
        }
    }, [location])

    const handlePermissions = async (permissions: string[]) => {
        let savePermissions: { [x: string]: string[] } = {}
        let tempMainNode = ""
        permissions.map(permission => {
            let mainNode = permission.split("_")[0]
            Object.assign(savePermissions, {
                ...savePermissions,
                [mainNode]: mainNode === tempMainNode ? [...savePermissions[mainNode], permission] : [permission]
            })
            tempMainNode = mainNode
        })
        setPermissions(savePermissions)
    };

    const handleLogin = async (data: ILoginBody) => {
        try {
            const response = await authService.login(data)
            login(response);
            handlePermissions(response.content.user.role.permissions);
        } catch (error) {
            if (error instanceof AxiosError) {
                setMessage(error.response?.data?.content?.message ?? t("genericError"));
            } else {
                setMessage(t("genericError"));
            }
            setResponseType("error");
            setOpenSnackbar(true);
        }
    };

    const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElLang(event.currentTarget);
    };

    const handleCloseLangMenu = (option: { label: string, value: string }) => {
        if (locale !== option.value) {
            setLanguage(option.value)
            changeLanguage(option.value);
        }
        setAnchorElLang(null);
    };

    const handleStartChangePassword = async (data: IStartChangePasswordBody) => {
        try {
            await authService.startChangePassword(data)
            setMessage(t("login.sentPasswordRequest"));
            setResponseType("success");
            setOpenSnackbar(true);
            setOpenDialog(false);
            startChangePasswordForm.initialValues = initialValuesStartChangePassword
        } catch (error) {
            if (error instanceof AxiosError) {
                setMessage(error.response?.data?.content?.message ?? t("genericError"));
            } else {
                setMessage(t("genericError"));
            }
            setResponseType("error");
            setOpenSnackbar(true);
        }
    };

    // dialog props
    const dialogProps: ICustomDialogProps = {
        type: "info",
        close: () => setOpenDialog(false),
        title: t("login.forgotPasswordTitle"),
        open: openDialog,
        fullWidth: true
    };

    return loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", width: "100vw" }}>
            <Box sx={{ width: { xs: 200, sm: 300, md: 400 }, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                    component="img"
                    src={Logo}
                    sx={{ mb: 2, width: { xs: 300, sm: 448, md: 500 } }}
                />
                <CustomLoader />
            </Box>
        </Box>
    ) : <Box sx={
        {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            backgroundRepeat: "none",
            backgroundSize: "cover",
            backgroundImage: `url(${Background})`,
            backgroundPosition: "center"
        }
    }>
        <CustomSnackbar type={responseType} message={message} open={openSnackbar} onClose={() => setOpenSnackbar(false)} />
        <CustomDialog {...dialogProps}>
            <Typography variant="body2" sx={{ mb: 1 }}>{t("login.forgotPasswordContent")}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <CustomForm
                    {...startChangePasswordForm}
                    initialValues={initialValuesStartChangePassword}
                    submit={handleStartChangePassword}
                    labelButtonSubmit={t("login.sendRequest")}
                    customStyles={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                    isDialog={false}
                />
                {/* <Typography variant="body1" sx={{ fontWeight: 600 }}>amministrazione@bee2gether.it</Typography> */}
            </Box>
        </CustomDialog>
        <Card sx={{ width: 380, p: 1, m: 2, position: "relative" }}>
            <Tooltip title={t("header.selectLanguage")} sx={{ position: "absolute", top: 8, right: 12 }}>
                <IconButton onClick={handleOpenLangMenu} color="primary">
                    <Language />
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElLang}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElLang)}
                onClose={handleCloseLangMenu}
            >
                {lang.map((option) => (
                    <MenuItem key={option.label} onClick={() => handleCloseLangMenu(option)}>
                        <Box sx={{
                            width: 28,
                            height: 19,
                            mr: 1
                        }}>{option.flag}</Box>
                        <Typography textAlign="center">{option.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>
            <Box sx={{ display: "flex", justifyContent: "center", m: 2, mb: 0 }}>
                <Box
                    component="img"
                    sx={{ height: "160px" }}
                    src={LoginLogo}
                    title="bee2gether-logo"
                ></Box>
            </Box>
            <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                <Typography gutterBottom variant="h6" textAlign="center" component="div">
                    {t("login.title")}
                </Typography>
                <CustomForm
                    {...signInForm}
                    initialValues={initialValuesSignIn}
                    submit={handleLogin}
                    labelButtonSubmit="Login"
                    customStyles={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                    isDialog={false}
                />
                <Box sx={{ mt: 1, mb: 1, display: "flex", justifyContent: "center" }}>
                    <Button variant="text" component="a" onClick={() => setOpenDialog(true)}>
                        {t("login.forgotPasswordButton")}
                    </Button>
                </Box>
                <Divider />
                <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" color="success" component="a" href={"register"}>
                        {t("login.register")}
                    </Button>
                </Box>
                <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" component="a" href={"project-preview"}>
                        {t("aboutProject")}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    </Box>
}

export default Login;